import { useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { UserPlus, PlusCircle, MoreVertical, AlertTriangle } from 'lucide-react';
import { useWizard } from '../WizardProvider';
import WizardShell from '../WizardShell';
import UserCard from '../components/UserCard';
import WizardDialog from '../components/WizardDialog';
import { buildUserSchema } from '../schemas/user.schema';
import { USER_ROLE_LABELS } from '../types';
import { DELETE_USER_TITLE, DELETE_USER_BODY, USERS_INCOMPLETE_HINT } from '../copy';

export default function Step4Users({ onExit }: { onExit: () => void }) {
  const { state, dispatch } = useWizard();

  // The DYNAMIC schema, not the static shape: a user created before an ML integration was
  // added in a Step 2 round-trip is missing that LOS's required user name, and the static
  // shape would wave it through (found live on 2/9 — the identities never got written).
  const mlConsumerSelected = state.losIntegrations.some((los) => los.type === 'ml-consumer');
  const mlMortgageSelected = state.losIntegrations.some((los) => los.type === 'ml-mortgage');
  const activeUserSchema = useMemo(
    () =>
      buildUserSchema({
        requireMlConsumerUserName: mlConsumerSelected,
        requireMlMortgageUserName: mlMortgageSelected,
      }),
    [mlConsumerSelected, mlMortgageSelected],
  );

  // The rows failing that schema drive both the Next gate and the visible flags below — a
  // disabled button alone would not say WHICH user needs editing, or why.
  const incompleteIds = useMemo(
    () =>
      new Set(
        state.users.filter((u) => !activeUserSchema.safeParse(u).success).map((u) => u.id),
      ),
    [state.users, activeUserSchema],
  );

  // The design's Team Setup: saved users render as table rows; adding and editing happen in the
  // Add User / Edit User dialog (UserCard); each row's actions live behind its ⋮ popover.
  const [editor, setEditor] = useState<{ id: string; isNew: boolean } | null>(null);
  // Anchored fixed, not absolute: the table's overflow container would clip an absolute menu.
  const [menuFor, setMenuFor] = useState<{ id: string; top: number; right: number } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Each editor session's verdict (duplicate email, check in flight, invalid on close), reported
  // by the dialog because only it runs the async duplicate check — the schema below cannot see it.
  const [blockedIds, setBlockedIds] = useState<Record<string, boolean>>({});
  const setBlocked = (id: string, blocked: boolean) =>
    setBlockedIds((prev) => (prev[id] === blocked ? prev : { ...prev, [id]: blocked }));
  const removeUser = (id: string) => {
    setBlockedIds((prev) => {
      const rest = { ...prev };
      delete rest[id];
      return rest;
    });
    dispatch({ type: 'removeUser', id });
  };

  const addUser = () => {
    // The id is minted here so the editor can open on the row the reducer is about to create.
    const id = uuid();
    dispatch({ type: 'addUser', id });
    setEditor({ id, isNew: true });
  };

  const closeEditor = () => {
    if (!editor) return;
    // A brand-new user closed while still incomplete was abandoned, not added — drop it so no
    // half-empty row lands in the table. An EDITED user always keeps its row (changes commit on
    // every keystroke); if it was left invalid, its verdict keeps blocking Next.
    const user = state.users.find((u) => u.id === editor.id);
    if (editor.isNew && user && !activeUserSchema.safeParse(user).success) removeUser(editor.id);
    setEditor(null);
  };

  // Zero users is fine (AC17.1) — every() is vacuously true on []. The dialog commits on every
  // keystroke, so its primary action is not the door the data passes through — this gate is
  // (AC9: "the user remains on Step 4").
  // Verdicts are consulted only for rows that still exist: the editor's unmount reports its
  // final verdict AFTER a discarded new user was already removed, and that stale entry must
  // not hold the gate forever.
  const canAdvance = incompleteIds.size === 0 && !state.users.some((u) => blockedIds[u.id]);

  const editingUser = editor ? state.users.find((u) => u.id === editor.id) : undefined;

  return (
    <WizardShell title="Team Setup" canAdvance={canAdvance} onExit={onExit}>
      {state.users.length === 0 ? (
        <div className="card p-10 flex flex-col items-center text-center">
          <UserPlus className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No users added yet</p>
          <button type="button" className="btn-primary mt-4" onClick={addUser}>
            Add User
          </button>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head text-left">
                  <th className="px-3 py-3 text-xs font-semibold text-white">Name</th>
                  <th className="px-3 py-3 text-xs font-semibold text-white">Email</th>
                  <th className="px-3 py-3 text-xs font-semibold text-white">Role</th>
                  <th className="px-3 py-3 text-xs font-semibold text-white">Products</th>
                  <th className="px-3 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="px-3 py-3 text-gray-900 dark:text-gray-100">
                      {`${user.firstName} ${user.lastName}`.trim() || '—'}
                      {incompleteIds.has(user.id) && (
                        <AlertTriangle
                          aria-label="Missing required information"
                          className="ml-1.5 inline h-4 w-4 align-text-bottom text-amber-500"
                        />
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-900 dark:text-gray-100">{user.email || '—'}</td>
                    <td className="px-3 py-3 text-gray-900 dark:text-gray-100">
                      {user.role ? USER_ROLE_LABELS[user.role] : '—'}
                    </td>
                    {/* null = inherits everything; a record means access was edited (OM-3946). */}
                    <td className="px-3 py-3 text-gray-900 dark:text-gray-100">
                      {user.productAccess === null ? 'All' : 'Edited'}
                    </td>
                    <td className="relative px-3 py-3 text-right">
                      <button
                        type="button"
                        aria-label="User actions"
                        aria-haspopup="menu"
                        className="btn-icon"
                        onClick={(e) => {
                          if (menuFor?.id === user.id) {
                            setMenuFor(null);
                            return;
                          }
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenuFor({
                            id: user.id,
                            top: r.bottom + 4,
                            right: Math.max(window.innerWidth - r.right, 8),
                          });
                        }}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {menuFor?.id === user.id && (
                        <>
                          {/* Invisible backdrop: any click outside the popover closes it. */}
                          <button
                            type="button"
                            aria-hidden="true"
                            tabIndex={-1}
                            className="fixed inset-0 z-10 cursor-default"
                            onClick={() => setMenuFor(null)}
                          />
                          <div
                            role="menu"
                            className="card fixed z-20 w-28 p-1 text-left shadow-lg"
                            style={{ top: menuFor.top, right: menuFor.right }}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              className="block w-full rounded px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                setMenuFor(null);
                                setEditor({ id: user.id, isNew: false });
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="block w-full rounded px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                setMenuFor(null);
                                setDeleting(user.id);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
          {incompleteIds.size > 0 && (
            <p className="mt-3 flex items-start gap-1.5 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {USERS_INCOMPLETE_HINT}
            </p>
          )}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
              onClick={addUser}
            >
              <PlusCircle className="h-4 w-4" aria-hidden="true" /> Add User
            </button>
          </div>
        </>
      )}

      {editor && editingUser && (
        <UserCard
          user={editingUser}
          isNew={editor.isNew}
          onChange={(data) => dispatch({ type: 'updateUser', id: editor.id, data })}
          onClose={closeEditor}
          onBlockedChange={(blocked) => setBlocked(editor.id, blocked)}
        />
      )}

      <WizardDialog
        open={deleting !== null}
        title={DELETE_USER_TITLE}
        body={DELETE_USER_BODY}
        confirmLabel="Delete User"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (deleting) removeUser(deleting);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </WizardShell>
  );
}
